import React, { useEffect, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableFooter,
    TableRow,
    TableCell,
    TableContainer,
    TextField,
    TablePagination,
    MenuItem,
    Grid,
    Button,
    Card
} from "@mui/material";
import { FilterAlt as FilterIcon } from "@mui/icons-material";
import { collection, query, orderBy, getDocs } from "firebase/firestore";
import { db } from "../../firebase";

function Throughreport() {
    const [rawTrips, setRawTrips] = useState([]);
    const [filteredTrips, setFilteredTrips] = useState([]);
    const [throughs, setThroughs] = useState([]);

    const [search, setSearch] = useState("");
    const [selectedThrough, setSelectedThrough] = useState("");
    const [fromDate, setFromDate] = useState(new Date().toISOString().split("T")[0]);
    const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [loading, setLoading] = useState(false);

    // Initial load for filter selections only
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const snap = await getDocs(collection(db, "throughdetails"));
                setThroughs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (err) {
                console.error("Error fetching reference details:", err);
            }
        };
        fetchFilters();
    }, []);

    // Manual on-demand filter process
    const handleFetchData = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, "trips"), orderBy("grossDate", "desc"));
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            setRawTrips(data);
            processFilters(data);
        } catch (error) {
            console.error("Error retrieving records:", error);
        } finally {
            setLoading(false);
        }
    };

    const processFilters = (data) => {
        const result = data.filter((trip) => {
            const tripDate = trip.grossDate?.toDate ? trip.grossDate.toDate() : null;
            const from = new Date(fromDate);
            from.setHours(0, 0, 0, 0);

            const to = new Date(toDate);
            to.setHours(23, 59, 59, 999);

            const dateMatch = !tripDate || (tripDate >= from && tripDate <= to);
            const throughMatch = !selectedThrough || trip.throughId === selectedThrough;
            const searchMatch = JSON.stringify(trip).toLowerCase().includes(search.toLowerCase());

            return dateMatch && throughMatch && searchMatch;
        });

        setFilteredTrips(result);
        setPage(0);
    };

    // Keep dynamic search filtering snappy if data is already fetched
    useEffect(() => {
        if (rawTrips.length > 0) {
            processFilters(rawTrips);
        }
    }, [search]);

    // Summary calculations
    const totalNetWeight = filteredTrips.reduce((sum, row) => sum + Number(row.netWeight || 0), 0);
    const totalAmount = filteredTrips.reduce((sum, row) => sum + Number(row.totalAmount || 0), 0);

    return (
        <Box sx={{ p: 2, bgcolor: "#f8f9fa", minHeight: "100vh" }}>
            {/* Header Area */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", letterSpacing: "-0.5px" }}>
                        Reference Report
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        Manage and track logistics metrics systematically
                    </Typography>
                </Box>
            </Box>

            {/* Filter Panel */}
            <Card variant="outlined" sx={{ p: 2, mb: 2, borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", bgcolor: "#fff" }}>
                <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="From Date"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="To Date"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Reference Collection"
                            value={selectedThrough}
                            onChange={(e) => setSelectedThrough(e.target.value)}
                        >
                            <MenuItem value="">All References</MenuItem>
                            {throughs.map((item) => (
                                <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Global Search"
                            size="small"
                            fullWidth
                            placeholder="Type variant details..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Button
                            variant="contained"
                            fullWidth
                            disableElevation
                            startIcon={<FilterIcon />}
                            onClick={handleFetchData}
                            disabled={loading}
                            sx={{ borderRadius: "6px", textTransform: "none", py: 0.7, fontWeight: 600, bgcolor: "#2563eb", '&:hover': { bgcolor: "#1d4ed8" } }}
                        >
                            {loading ? "Fetching..." : "Apply Filters"}
                        </Button>
                    </Grid>
                </Grid>
            </Card>

            {/* Main Data Sheet */}
            <Paper variant="outlined" sx={{ borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                <TableContainer sx={{ maxHeight: "calc(100vh - 260px)" }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow sx={{ "& th": { bgcolor: "#f1f5f9", color: "#475569", fontWeight: 600, fontSize: "11px", textTransform: "uppercase" } }}>
                                <TableCell width={50}>SR</TableCell>
                                <TableCell>Challan</TableCell>
                                <TableCell>Ticket</TableCell>
                                <TableCell>Reference</TableCell>
                                <TableCell>Customer</TableCell>
                                <TableCell>Vehicle No</TableCell>
                                <TableCell align="right">Net Weight</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell>Timestamp Date</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filteredTrips.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 6, color: "text.secondary" }}>
                                        No metrics loaded. Configure filter conditions and click "Apply Filters".
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredTrips.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                                    <TableRow key={row.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 }, "& .MuiTableCell-root": { py: 0.75, fontSize: "12.5px" } }}>
                                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>{row.challan}</TableCell>
                                        <TableCell>{row.ticketNo}</TableCell>
                                        <TableCell color="text.secondary">{row.throughName}</TableCell>
                                        <TableCell>{row.customerName}</TableCell>
                                        <TableCell sx={{ fontFamily: "monospace" }}>{row.vehicleNo}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 500 }}>{Number(row.netWeight || 0).toLocaleString()}</TableCell>
                                        <TableCell align="right" sx={{ color: "success.main", fontWeight: 600 }}>
                                            ₹{Math.round(row.totalAmount || 0).toLocaleString()}
                                        </TableCell>
                                        <TableCell color="text.secondary">
                                            {row.grossDate?.toDate ? row.grossDate.toDate().toLocaleString() : "-"}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>

                        {/* Sticky Columns Summary Row */}
                        {filteredTrips.length > 0 && (
                            <TableFooter sx={{ position: "sticky", bottom: 0, bgcolor: "#f8fafc", zIndex: 2, borderTop: "2px solid #e2e8f0" }}>
                                <TableRow sx={{ "& td": { fontWeight: "700 !important", color: "#0f172a", fontSize: "13px", py: 1.5 } }}>
                                    <TableCell colSpan={5}>Total Trips: {filteredTrips.length}</TableCell>
                                    <TableCell align="right">Summary Total:</TableCell>
                                    <TableCell align="right" sx={{ color: "#1e3a8a" }}>{Math.round(totalNetWeight).toLocaleString()}</TableCell>
                                    <TableCell align="right" sx={{ color: "#15803d" }}>₹{Math.round(totalAmount).toLocaleString()}</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableFooter>
                        )}
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={filteredTrips.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    sx={{ borderTop: "1px solid #e2e8f0", bgcolor: "#fff" }}
                />
            </Paper>
        </Box>
    );
}

export default Throughreport;